import type { Client } from '../../types';
import { CARDS, STATS } from '../../styles/classNames';
import { formatPhoneForDisplay } from '@/shared/utils/phone';

interface ClientListProps {
  clients: Client[];
  assignmentCounts?: Record<string, number>;
  sessionCounts?: Record<string, number>;
  onSelect?: (clientId: string) => void;
  search?: string;
  onSearchChange?: (value: string) => void;
}

export function ClientList({
  clients,
  assignmentCounts = {},
  sessionCounts = {},
  onSelect,
  search = '',
  onSearchChange,
}: ClientListProps) {
  // Filter clients by search
  const filteredClients = clients.filter((client) => {
    if (!search) return true;
    const searchLower = search.toLowerCase();
    return (
      client.displayName?.toLowerCase().includes(searchLower) ||
      client.phone?.toLowerCase().includes(searchLower)
    );
  });

  return (
    <div className="flex-1 flex flex-col min-h-0">
      {onSearchChange && (
        <input
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder="Search clients..."
          className="form-field max-w-none mb-3 shrink-0"
        />
      )}
      <div className="space-y-3 flex-1 overflow-auto pr-1 min-h-0">
        {filteredClients.length === 0 && (
          <p className="text-sm text-slate-400">
            {search ? 'No clients match your search.' : 'No clients yet. Add one above.'}
          </p>
        )}
        {filteredClients.map((client) => (
          <div
            key={client.id}
            onClick={() => onSelect?.(client.id)}
            data-testid="trainer-client-row"
            data-client-id={client.id}
            className={`${CARDS.listItem} ${
              onSelect ? 'cursor-pointer hover:border-primary-500/50 hover:from-primary-50 dark:hover:from-primary-500/10 transition-all' : ''
            }`}
          >
            <div className="space-y-3">
              {/* Header with name and stats */}
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-sm md:text-base text-gray-900 dark:text-white">
                    {client.displayName || 'Client'}
                  </p>
                  <p className="text-xs text-gray-600 dark:text-slate-400 truncate">
                    {formatPhoneForDisplay(client.phone)}
                  </p>
                </div>
                <div className={STATS.withDot}>
                  <span className={`whitespace-nowrap ${
                    client.status === 'linked'
                      ? 'text-emerald-600 dark:text-emerald-300'
                      : client.status === 'declined'
                        ? 'text-rose-600 dark:text-rose-300'
                        : 'text-amber-600 dark:text-amber-300'
                  }`}>
                    {client.status}
                  </span>
                  <span className={STATS.dot} />
                  <span className="whitespace-nowrap">
                    {assignmentCounts[client.id] || 0} queued
                  </span>
                  <span className={STATS.dot} />
                  <span className="whitespace-nowrap">
                    {sessionCounts[client.id] || 0} sessions
                  </span>
                </div>
              </div>
              <div>
                <p className="text-xs text-gray-600 dark:text-slate-400">
                  {client.status === 'linked'
                    ? 'Client accepted the invite and is linked.'
                    : client.status === 'declined'
                      ? 'Client declined the invite.'
                      : 'Waiting for client to sign in and accept the invite.'}
                </p>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
